import type { ArticleSummary, CreateShareInput, Language, ShareRecord } from '@rabbit/shared';
import { Compass, HelpCircle, Route } from 'lucide-react';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createShare, getArticles, getDiscover, getRelated, getRuntimeConfig, getShare } from './api/client';
import { ArticleCard } from './components/ArticleCard';
import { Onboarding } from './components/Onboarding';
import { PathSheet } from './components/PathSheet';
import { SettingsSheet } from './components/SettingsSheet';
import { SharedJourney } from './components/SharedJourney';
import { copyFor } from './i18n';
import { activeLevel, currentArticle, explorationReducer, initialExplorationState, journeyPath } from './state/exploration';
import { telegram } from './telegram/adapter';

const ONBOARDING_KEY = 'rabbit:onboarding:v1';

interface SharedPresentation {
  record: Extract<ShareRecord, { kind: 'journey' }>;
  articles: ArticleSummary[];
}

function shareViaTelegram(text: string, link: string): void {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
  telegram.openTelegramLink(url);
}

function articleKey(article: ArticleSummary): string {
  return `${article.lang}:${article.title}`;
}

export default function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('rabbit:language');
    return saved === 'ru' || saved === 'en' ? saved : telegram.getLanguage() ?? 'en';
  });
  const copy = copyFor(language);
  const [state, dispatch] = useReducer(explorationReducer, initialExplorationState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pathOpen, setPathOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [onboarding, setOnboarding] = useState<boolean | null>(null);
  const [sharing, setSharing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [shared, setShared] = useState<SharedPresentation | null>(null);
  const [relatedResults, setRelatedResults] = useState(() => new Map<string, ArticleSummary[]>());
  const relatedCache = useRef(new Map<string, Promise<ArticleSummary[]>>());
  const seen = useRef(new Set<string>());
  const wheelLocked = useRef(false);
  const rootRequest = useRef<AbortController | null>(null);
  const discoverPreload = useRef<Promise<void> | null>(null);
  const relatedPreloads = useRef(new Set<string>());

  const level = activeLevel(state);
  const article = currentArticle(state);
  const previousArticle = level.items[level.index - 1];
  const nextArticle = level.items[level.index + 1];
  const exploreArticle = article
    ? relatedResults.get(articleKey(article))?.find((item) => !seen.current.has(articleKey(item)) && item.title !== article.title)
    : undefined;
  const path = journeyPath(state);
  const depth = state.levels.length - 1;

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const relatedFor = useCallback((target: ArticleSummary) => {
    const key = articleKey(target);
    const existing = relatedCache.current.get(key);
    if (existing) return existing;
    const request = getRelated(target.lang, target.title);
    relatedCache.current.set(key, request);
    void request.catch(() => relatedCache.current.delete(key));
    return request;
  }, []);

  const prefetchArticleRelated = useCallback((target: ArticleSummary) => {
    const key = articleKey(target);
    if (relatedResults.has(key)) return;
    void relatedFor(target)
      .then((items) => {
        setRelatedResults((current) => {
          if (current.has(key)) return current;
          const next = new Map(current);
          next.set(key, items);
          return next;
        });
      })
      .catch(() => undefined);
  }, [relatedFor, relatedResults]);

  const loadDiscover = useCallback(async (reset = false) => {
    rootRequest.current?.abort();
    const controller = new AbortController();
    rootRequest.current = controller;
    setLoading(true);
    setError(null);
    try {
      const items = await getDiscover(language, controller.signal);
      dispatch({ type: reset ? 'RESET_ROOT' : 'SET_ROOT', items });
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return;
      setError(reason instanceof Error ? reason.message : copy.errorText);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [copy.errorText, language]);

  const preloadDiscover = useCallback(() => {
    if (discoverPreload.current) return discoverPreload.current;
    const request = getDiscover(language)
      .then((items) => dispatch({ type: 'SET_ROOT', items }))
      .catch(() => undefined)
      .finally(() => { discoverPreload.current = null; });
    discoverPreload.current = request;
    return request;
  }, [language]);

  const preloadRelated = useCallback((levelDepth: number, anchor: ArticleSummary, existingItems: ArticleSummary[]) => {
    const key = `${levelDepth}:${anchor.lang}:${anchor.title}`;
    if (relatedPreloads.current.has(key)) return;
    relatedPreloads.current.add(key);
    const existing = new Set(existingItems.map((item) => articleKey(item)));
    void relatedFor(anchor)
      .then((items) => items.filter((item) => !seen.current.has(articleKey(item)) && !existing.has(articleKey(item))))
      .then((items) => dispatch({ type: 'APPEND_ITEMS', level: levelDepth, items, exhausted: items.length === 0 }))
      .catch(() => undefined)
      .finally(() => { relatedPreloads.current.delete(key); });
  }, [relatedFor]);

  useEffect(() => {
    const cleanup = telegram.initialize();
    void telegram.storageGet(ONBOARDING_KEY).then((value) => setOnboarding(value !== 'done'));
    if (!localStorage.getItem('rabbit:language') && !telegram.getLanguage()) {
      void getRuntimeConfig().then(({ defaultLanguage }) => setLanguage(defaultLanguage)).catch(() => undefined);
    }
    if (telegram.getStartParam()?.startsWith('s_')) setLoading(false);
    else void loadDiscover(true);
    return () => { cleanup(); rootRequest.current?.abort(); };
  }, [loadDiscover]);

  useEffect(() => {
    const start = telegram.getStartParam();
    if (!start?.startsWith('s_')) return;
    const id = start.slice(2);
    void getShare(id).then(async (record) => {
      setLanguage(record.payload.lang);
      if (record.kind === 'article') {
        const articles = await getArticles(record.payload.lang, [record.payload.title]);
        if (articles.length) dispatch({ type: 'RESET_ROOT', items: articles });
      } else {
        const titles = [...record.payload.titles, record.payload.currentTitle];
        const articles = await getArticles(record.payload.lang, titles);
        setOnboarding(false);
        setShared({ record, articles });
      }
    }).catch(() => showToast(copy.errorText));
  }, [copy.errorText, showToast]);

  useEffect(() => {
    if (!article) return;
    seen.current.add(articleKey(article));
    if (seen.current.size > 500) seen.current = new Set([...seen.current].slice(-350));
    const upcoming = level.items.slice(Math.max(0, level.index - 1), level.index + 4);
    for (const next of upcoming) {
      if (!next.thumbnailUrl) continue;
      const image = new Image();
      image.src = next.originalImageUrl ?? next.thumbnailUrl;
    }
  }, [article, level.index, level.items]);

  useEffect(() => {
    const upcoming = level.items.slice(level.index, level.index + 4);
    for (const item of upcoming) prefetchArticleRelated(item);
  }, [level.index, level.items, prefetchArticleRelated]);

  useEffect(() => {
    const remaining = level.items.length - level.index - 1;
    if (!level.anchor) {
      if (!loading && level.items.length > 0 && remaining <= 3) void preloadDiscover();
      return;
    }
    if (!level.exhausted && remaining <= 3) preloadRelated(depth, level.anchor, level.items);
  }, [depth, level.anchor, level.exhausted, level.index, level.items, level.items.length, loading, preloadDiscover, preloadRelated]);

  const goNext = useCallback(() => {
    if (loading) return;
    setError(null);
    if (level.index < level.items.length - 1) {
      dispatch({ type: 'NEXT' });
      telegram.haptic('soft');
      return;
    }
    if (level.anchor) {
      if (level.exhausted) {
        setError(copy.emptyTitle);
        return;
      }
      const existing = new Set(level.items.map((item) => articleKey(item)));
      setLoading(true);
      void relatedFor(level.anchor).then((items) => {
        const fresh = items.filter((item) => !seen.current.has(articleKey(item)) && !existing.has(articleKey(item)));
        dispatch({ type: 'APPEND_ITEMS', level: depth, items: fresh, exhausted: fresh.length === 0 });
        if (fresh.length) dispatch({ type: 'NEXT' });
        else setError(copy.emptyTitle);
      }).catch(() => setError(copy.errorText)).finally(() => setLoading(false));
    } else {
      setLoading(true);
      void (discoverPreload.current ?? preloadDiscover()).then(() => dispatch({ type: 'NEXT' })).finally(() => setLoading(false));
    }
  }, [copy.emptyTitle, copy.errorText, depth, level.anchor, level.exhausted, level.index, level.items, level.items.length, loading, preloadDiscover, relatedFor]);

  const goPrevious = useCallback(() => dispatch({ type: 'PREVIOUS' }), []);
  const goBack = useCallback(() => { if (depth > 0) { dispatch({ type: 'BACK' }); telegram.haptic('soft'); } }, [depth]);
  const goHome = useCallback(() => { dispatch({ type: 'HOME' }); telegram.haptic('light'); }, []);

  const explore = useCallback(() => {
    if (!article) return;
    const cached = relatedResults.get(articleKey(article));
    const cachedFresh = cached?.filter((item) => !seen.current.has(articleKey(item)) && item.title !== article.title) ?? [];
    if (cachedFresh.length) {
      setError(null);
      dispatch({ type: 'EXPLORE', anchor: article, items: cachedFresh });
      telegram.haptic('medium');
      return;
    }
    setLoading(true);
    setError(null);
    void relatedFor(article).then((items) => {
      const fresh = items.filter((item) => !seen.current.has(articleKey(item)) && item.title !== article.title);
      if (!fresh.length) {
        setError(copy.emptyTitle);
        telegram.notify('warning');
        return;
      }
      dispatch({ type: 'EXPLORE', anchor: article, items: fresh });
      telegram.haptic('medium');
    }).catch(() => setError(copy.errorText)).finally(() => setLoading(false));
  }, [article, copy.emptyTitle, copy.errorText, relatedFor, relatedResults]);

  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPathOpen(false); setSettingsOpen(false); return; }
      if (pathOpen || settingsOpen || onboarding || shared) return;
      const actions: Record<string, () => void> = { ArrowUp: goNext, ArrowDown: goPrevious, ArrowRight: explore, ArrowLeft: goBack };
      const action = actions[event.key];
      if (action) { event.preventDefault(); action(); }
    };
    const wheel = (event: WheelEvent) => {
      if (pathOpen || settingsOpen || onboarding || shared || wheelLocked.current || Math.abs(event.deltaY) < 18) return;
      wheelLocked.current = true;
      event.preventDefault();
      if (event.deltaY > 0) goNext(); else goPrevious();
      window.setTimeout(() => { wheelLocked.current = false; }, 560);
    };
    window.addEventListener('keydown', keyboard);
    window.addEventListener('wheel', wheel, { passive: false });
    return () => { window.removeEventListener('keydown', keyboard); window.removeEventListener('wheel', wheel); };
  }, [explore, goBack, goNext, goPrevious, onboarding, pathOpen, settingsOpen, shared]);

  const createAndShare = useCallback(async (input: CreateShareInput, text: string) => {
    setSharing(true);
    try {
      const result = await createShare(input);
      shareViaTelegram(text, result.deepLink || result.webLink);
      showToast(copy.articleShared);
    } catch {
      telegram.notify('error');
      showToast(copy.shareFailed);
    } finally {
      setSharing(false);
    }
  }, [copy.articleShared, copy.shareFailed, showToast]);

  const shareArticle = () => {
    if (!article) return;
    void createAndShare({ kind: 'article', payload: { lang: article.lang, title: article.title } }, `Interesting Wikipedia rabbit hole: ${article.displayTitle}`);
  };
  const shareJourney = () => {
    if (!article || !path.length) return;
    void createAndShare({ kind: 'journey', payload: { lang: article.lang, titles: path.map((item) => item.title), currentTitle: article.title } }, `I fell down this Wikipedia rabbit hole:\n\n${path.map((item) => item.displayTitle).join(' → ')}\n\nContinue from where I ended:`);
  };

  const completeOnboarding = () => { setOnboarding(false); void telegram.storageSet(ONBOARDING_KEY, 'done'); };
  const changeLanguage = (next: Language) => {
    localStorage.setItem('rabbit:language', next);
    relatedCache.current.clear();
    setRelatedResults(new Map());
    seen.current.clear();
    setLanguage(next);
    setSettingsOpen(false);
  };

  const parentTitle = depth > 1 ? state.levels[depth - 1]?.anchor?.displayTitle : state.levels[1]?.anchor?.displayTitle;
  const appClass = useMemo(() => `app-shell ${pathOpen || settingsOpen || onboarding || shared ? 'has-overlay' : ''}`, [onboarding, pathOpen, settingsOpen, shared]);

  return (
    <div className={appClass}>
      <header className="topbar">
        <button type="button" className="brand-button" onClick={depth ? goBack : goHome} aria-label={depth ? copy.back : copy.home}>
          {depth > 0 && <span aria-hidden="true">←</span>}<span>{depth > 0 ? level.anchor?.displayTitle : copy.brand}</span>
        </button>
        <div className="top-actions">
          {depth > 0 && <span className="depth-pill">{depth}</span>}
          <button className="icon-button" type="button" aria-label={copy.path} onClick={() => setPathOpen(true)}><Route /></button>
          <button className="icon-button" type="button" aria-label={copy.help} onClick={() => setSettingsOpen(true)}><HelpCircle /></button>
          <button className="icon-button discover-button" type="button" aria-label={copy.home} onClick={goHome}><Compass /></button>
        </div>
      </header>

      {!article && loading && <div className="article-skeleton" aria-label={copy.loadingInitial}><div/><section><span/><span/><span/><span/></section></div>}
      {!article && error && <div className="error-state"><span>W</span><h1>{copy.errorTitle}</h1><p>{copy.errorText}</p><button className="primary-button" type="button" onClick={() => void loadDiscover(true)}>{copy.retry}</button></div>}
      {article && <ArticleCard key={`${article.lang}-${article.id}-${depth}`} article={article} previousArticle={previousArticle} nextArticle={nextArticle} exploreArticle={exploreArticle} copy={copy} anchorTitle={level.anchor?.displayTitle} parentTitle={parentTitle} depth={depth} loading={loading} onNext={goNext} onPrevious={goPrevious} onExplore={explore} onBack={goBack} onShare={shareArticle} onOpen={() => telegram.openExternalLink(article.wikipediaUrl)} />}

      {error && article && <div className="inline-error"><strong>{error === copy.emptyTitle ? copy.emptyTitle : copy.errorTitle}</strong><span>{error === copy.emptyTitle ? copy.emptyText : copy.errorText}</span><button type="button" onClick={() => { setError(null); if (error !== copy.emptyTitle) explore(); }}>{copy.retry}</button></div>}
      {toast && <div className="toast" role="status">{toast}</div>}
      {pathOpen && <PathSheet path={path} current={article} copy={copy} sharing={sharing} onClose={() => setPathOpen(false)} onShare={shareJourney}/>}
      {settingsOpen && <SettingsSheet language={language} copy={copy} onLanguage={changeLanguage} onTutorial={() => { setSettingsOpen(false); setOnboarding(true); }} onClose={() => setSettingsOpen(false)}/>}
      {onboarding && <Onboarding copy={copy} onComplete={completeOnboarding}/>}
      {shared && <SharedJourney articles={shared.articles} pathLength={shared.record.payload.titles.length} copy={copy} onContinue={() => { dispatch({ type: 'HYDRATE_SHARED', articles: shared.articles, pathLength: shared.record.payload.titles.length }); setShared(null); }}/>}
    </div>
  );
}
